import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardContent, Avatar, Typography, Alert, Select, MenuItem, Box, List, ListItem, ListItemText, IconButton, Button, Chip } from '@mui/material';
import { blue } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';

const StudioProgramareCard = ({
  selectedSesiune,
  onChangeSesiune,
  linkedIssues,
  fisaProdusId,
  onUnlinkTask,
  onAlocare,
  skuJira,
}) => {
  const [fotograf, setFotograf] = useState('');
  const [alocariCount, setAlocariCount] = useState(null);
  const [sesiuniDb, setSesiuniDb] = useState([]);
  const [alocariList, setAlocariList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hadExistingAllocation, setHadExistingAllocation] = useState(false);

  const sesiuniGenerate = useMemo(() => {
    if (fotograf === 'intern') {
      return Array.from({ length: 299 }, (_, index) => 2 + index); // 2 - 300
    }
    if (fotograf === 'extern') {
      return Array.from({ length: 256 }, (_, index) => 245 + index); // 245 - 500
    }
    return [];
  }, [fotograf]);

  const handleChangeFotograf = (event) => {
    setFotograf(event.target.value);
    if (onChangeSesiune) {
      onChangeSesiune({ target: { value: '' } });
    }
  };

  const handleChangeSesiune = (event) => {
    if (onChangeSesiune) {
      onChangeSesiune(event);
    }
  };

  const existingAllocation = useMemo(
    () => alocariList.find((item) => item.sku === skuJira),
    [alocariList, skuJira]
  );

  const resolvedSelectedSesiune = useMemo(() => {
    if (selectedSesiune) {
      return selectedSesiune;
    }
    if (existingAllocation?.sesiune_nr) {
      return `Sesiunea ${existingAllocation.sesiune_nr}`;
    }
    return "";
  }, [selectedSesiune, existingAllocation]);

  const sesiuneNumber =
    resolvedSelectedSesiune && /\d+/.test(resolvedSelectedSesiune)
      ? Number(resolvedSelectedSesiune.match(/\d+/)[0])
      : null;

  const skuHasAllocation = alocariList.some((item) => item.sku === skuJira);
  const isMutare = hadExistingAllocation || skuHasAllocation;

  // Reset la schimbarea SKU-ului selectat
  useEffect(() => {
    setFotograf('');
    setAlocariList([]);
    setAlocariCount(null);
    setHadExistingAllocation(false);
    if (onChangeSesiune) {
      onChangeSesiune({ target: { value: '' } });
    }
  }, [skuJira]);

  useEffect(() => {
    const fetchSesiuniDb = async () => {
      try {
        const res = await fetch('https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni');
        if (!res.ok) throw new Error('request failed');
        const data = await res.json();
        setSesiuniDb(Array.isArray(data.rows) ? data.rows : []);
      } catch (err) {
        console.error('[sesiuni-db] failed', err);
        setSesiuniDb([]);
      }
    };
    fetchSesiuniDb();
  }, []);

  const sesiuniOptions = useMemo(() => {
    const fromDb = sesiuniDb
      .filter((s) => s.fotograf === fotograf)
      .map((s) => ({
        value: `Sesiunea ${s.sesiune_nr}`,
        label: `${s.sesiune_code} • Sesiunea ${s.sesiune_nr}`,
        sesiuneNr: s.sesiune_nr,
        code: s.sesiune_code,
      }));

    const generated = sesiuniGenerate.map((num) => {
      const code = `${fotograf === 'intern' ? 'INS' : 'EXS'}${num}`;
      return {
        value: `Sesiunea ${num}`,
        label: `${code} • Sesiunea ${num}`,
        sesiuneNr: num,
        code,
      };
    });

    const combined = [...fromDb, ...generated];
    const seen = new Set();
    return combined.filter((opt) => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [sesiuniDb, fotograf, sesiuniGenerate]);

  const currentSesiuneCode = useMemo(() => {
    const opt = sesiuniOptions.find((o) => o.value === resolvedSelectedSesiune);
    if (opt?.code) return opt.code;
    if (!fotograf || !sesiuneNumber) return '';
    return `${fotograf === 'intern' ? 'INS' : 'EXS'}${sesiuneNumber}`;
  }, [sesiuniOptions, resolvedSelectedSesiune, fotograf, sesiuneNumber]);

  const loadAlocariByCode = async (code) => {
    if (!code) {
      setAlocariCount(null);
      setAlocariList([]);
      return;
    }
    try {
      const res = await fetch(`https://server-9p4hx.kinsta.app/api/tasks/pozestudio/alocari?sesiuneCode=${encodeURIComponent(code)}`);
      if (!res.ok) throw new Error('request failed');
      const data = await res.json();
      const rows = Array.isArray(data.rows) ? data.rows : [];
      setAlocariCount(rows.length);
      setAlocariList(rows);
    } catch (err) {
      console.error('[alocari-load] failed', err);
      setAlocariCount(null);
      setAlocariList([]);
    }
  };

  useEffect(() => {
    const fetchAlocariCount = async () => {
      if (!fotograf || !sesiuneNumber) {
        setAlocariCount(null);
        setAlocariList([]);
        return;
      }
      await loadAlocariByCode(currentSesiuneCode);
    };
    fetchAlocariCount();
  }, [fotograf, sesiuneNumber, currentSesiuneCode]);

  const handleDeleteAlocare = async (alocare) => {
    if (!alocare?.id) return;
    const confirmDelete = window.confirm('Ștergi acest SKU din sesiune?');
    if (!confirmDelete) return;
    const prefix = fotograf === 'intern' ? 'INS' : 'EXS';
    const sesiuneCode = sesiuneNumber ? `${prefix}${sesiuneNumber}` : '';
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/alocari/${alocare.id}?sesiuneCode=${sesiuneCode}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('delete failed');
      const updated = alocariList.filter((item) => item.id !== alocare.id);
      setAlocariList(updated);
      setAlocariCount(updated.length);
    } catch (err) {
      console.error('[alocari-delete] failed', err);
      alert('Nu am putut șterge alocarea.');
    }
  };

  // Populate automat sesiunea/fotograful dacă SKU are deja alocare în DB
  useEffect(() => {
    const fetchBySku = async () => {
      if (!skuJira) return;
      try {
        const res = await fetch(`https://server-9p4hx.kinsta.app/api/tasks/pozestudio/alocari?sku=${encodeURIComponent(skuJira)}`);
        if (!res.ok) throw new Error('request failed');
        const data = await res.json();
        const rows = Array.isArray(data.rows) ? data.rows : [];
        setAlocariList(rows);
        setAlocariCount(rows.length);
        setHadExistingAllocation(rows.some((r) => r.sku === skuJira));
        if (rows.length) {
          const first = rows[0];
          const sesiuneLabel = `Sesiunea ${first.sesiune_nr}`;
          const photographer = first.fotograf;
          setFotograf(photographer);
          if (onChangeSesiune && sesiuneLabel !== selectedSesiune) {
            onChangeSesiune({ target: { value: sesiuneLabel } });
          }
        }
      } catch (err) {
        console.error('[alocari-by-sku] failed', err);
      }
    };
    fetchBySku();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuJira]);

  useEffect(() => {
    if (
      !selectedSesiune &&
      existingAllocation?.sesiune_nr &&
      typeof onChangeSesiune === 'function'
    ) {
      onChangeSesiune({
        target: { value: `Sesiunea ${existingAllocation.sesiune_nr}` },
      });
    }
  }, [selectedSesiune, existingAllocation, onChangeSesiune]);

  return (
    <Card
      sx={{
        width: '100%',
        mb: 2,
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0b1f3a 0%, #0f3057 45%, #124e78 100%)',
        color: '#e9f1ff',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}
    >
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: blue[500], boxShadow: '0 0 0 3px rgba(255,255,255,0.2)' }} aria-label="recipe">SP</Avatar>}
        title={<Typography sx={{ fontWeight: 800, letterSpacing: 0.4 }}>Studio Programare</Typography>}
        subheader={
          <Typography sx={{ color: '#9fc8ff', fontWeight: 500, letterSpacing: 0.2 }}>
            Selectează o sesiune și alocă produsul instant
          </Typography>
        }
      />
      <CardContent sx={{ background: 'rgba(255,255,255,0.04)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(120deg, rgba(159,200,255,0.18), rgba(255,255,255,0.05))',
            border: '1px solid rgba(255,255,255,0.08)',
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#b8d8ff' }}>
            Articole programate: {alocariCount !== null ? alocariCount : 'N/A'}
          </Typography>
          {alocariCount !== null && alocariCount > 9 && (
            <Alert
              severity="warning"
              sx={{
                mt: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(255,193,7,0.12)',
                color: '#ffca28',
                border: '1px solid rgba(255,193,7,0.35)',
              }}
            >
              Atenție: sesiunea are deja {alocariCount} produse alocate.
            </Alert>
          )}
          {resolvedSelectedSesiune && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip label={resolvedSelectedSesiune} size="small" sx={{ bgcolor: '#143d68', color: '#e9f1ff' }} />
              <Chip label={`Fotograf: ${fotograf || '—'}`} size="small" sx={{ bgcolor: '#0e2d4f', color: '#e9f1ff' }} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 200,
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 2,
              p: 1.5,
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#b8d8ff', fontWeight: 700, mb: 0.5 }}>
              Fotograf
            </Typography>
            <Select
              value={fotograf}
              onChange={handleChangeFotograf}
              displayEmpty
              fullWidth
              sx={{
                color: '#e9f1ff',
                '.MuiSelect-icon': { color: '#e9f1ff' },
              }}
            >
              <MenuItem value="" disabled>Selectează fotograful</MenuItem>
              <MenuItem value="intern">Intern</MenuItem>
              <MenuItem value="extern">Extern</MenuItem>
            </Select>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 200,
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 2,
              p: 1.5,
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#b8d8ff', fontWeight: 700, mb: 0.5 }}>
              Sesiune
            </Typography>
            <Select
              value={resolvedSelectedSesiune || ''}
              onChange={handleChangeSesiune}
              displayEmpty
              fullWidth
              disabled={!fotograf}
              sx={{
                color: '#e9f1ff',
                '.MuiSelect-icon': { color: '#e9f1ff' },
              }}
            >
              <MenuItem value="" disabled>{fotograf ? 'Selectează sesiunea' : 'Alege mai întâi fotograful'}</MenuItem>
              {sesiuniOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box
          sx={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 2,
            p: 2,
            border: '1px solid rgba(255,255,255,0.06)',
            minHeight: 120,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#b8d8ff', fontWeight: 700, mb: 1.5 }}>
            Articole programate
          </Typography>
          <List>
            {alocariList.length > 0 ? (
              alocariList.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAlocare(item)} sx={{ color: '#ff8b8b' }}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={item.denumire || item.sku || 'SKU'}
                    secondary={`SKU: ${item.sku || '—'} • Sesiune: ${item.sesiune_code || ''}`}
                    primaryTypographyProps={{ sx: { color: '#e9f1ff', fontWeight: 600 } }}
                    secondaryTypographyProps={{ sx: { color: '#9fc8ff' } }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem sx={{ borderRadius: 2, background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.08)' }}>
                <ListItemText primary="Nu există articole programate în această sesiune." primaryTypographyProps={{ sx: { color: '#9fc8ff' } }} />
              </ListItem>
            )}
          </List>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!onAlocare) return;
              setSaving(true);

                const sesiuneJiraKey = /^[A-Z][A-Z0-9]+-\d+$/i.test(
                  resolvedSelectedSesiune || ''
                )
                  ? resolvedSelectedSesiune
                  : '';
                const result = await onAlocare({
                  fotograf,
                  sesiuneNr: sesiuneNumber,
                  sesiuneLabel: sesiuneJiraKey,
                });

              setSaving(false);
              if (result?.ok) {
                const allocationId =
                  result.allocation?.allocationId || Date.now();
                const sesiuneCode =
                  result.allocation?.sesiuneCode ||
                  `${fotograf === 'intern' ? 'INS' : 'EXS'}${sesiuneNumber}`;
                const newEntry = {
                  id: allocationId,
                  sku: result.payload?.sku || skuJira || 'SKU',
                  denumire: result.payload?.denumire || 'Produs',
                  sesiune_code: sesiuneCode,
                  sesiune_nr: result.payload?.sesiuneNr,
                  fotograf,
                };
                setHadExistingAllocation(true);
                await loadAlocariByCode(sesiuneCode);
              }
            }}
            disabled={!fotograf || !resolvedSelectedSesiune || saving}
            sx={{
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontWeight: 700,
              background: 'linear-gradient(90deg, #3fb2ff, #6dd5fa)',
              boxShadow: '0 12px 30px rgba(63,178,255,0.35)',
            }}
          >
            {saving ? 'Se salvează...' : isMutare ? 'Mutare' : 'Alocare'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StudioProgramareCard;
